<template>
  <div>
    <!-- ── Page header ─────────────────────────────────────────────────────── -->
    <div class="page-section-header mb-1">
      <div class="d-flex align-items-center" style="gap: 12px">
        <div class="page-header-icon">
          <feather-icon icon="LayersIcon" size="20" />
        </div>
        <div>
          <h5 class="mb-25">Stack Summary</h5>
          <small class="text-muted">Bale count, mass and grade breakdown per stack in a warehouse</small>
        </div>
      </div>
    </div>

    <!-- ── Filters ─────────────────────────────────────────────────────────── -->
    <b-card no-body class="filter-card mb-1">
      <b-card-body>
        <b-row>
          <b-col cols="12" md="5" class="mb-md-0 mb-2">
            <location-filters
              size="sm"
              align="horizontal"
              @filter="changeFilter"
            />
            <small
              v-if="serverErrors && serverErrors.location_id"
              class="text-danger d-block mt-25"
            >{{ serverErrors.location_id[0] }}</small>
          </b-col>

          <b-col cols="12" md="3" class="mb-md-0 mb-2">
            <label class="filter-label" for="warehouse_id">Warehouse</label>
            <validation-provider
              #default="{ errors }"
              name="warehouse_id"
              rules=""
            >
              <b-form-select
                id="warehouse_id"
                v-model="stackGrade.warehouse_id"
                name="warehouse_id"
                size="sm"
                :disabled="!warehouses.length"
                @change="getStack"
              >
                <b-form-select-option :value="null">
                  Select...
                </b-form-select-option>
                <b-form-select-option
                  v-for="warehouse in warehouses"
                  :key="warehouse.id"
                  :value="warehouse.id"
                >
                  {{ warehouse.name.toUpperCase() }} ({{ warehouse.code }})
                </b-form-select-option>
              </b-form-select>
              <small class="text-danger">{{ errors[0] }}</small>
              <small
                v-if="serverErrors && serverErrors.warehouse_id"
                class="text-danger"
              >{{ serverErrors.warehouse_id[0] }}</small>
            </validation-provider>
          </b-col>

          <b-col cols="12" md="4" class="mb-md-0 mb-2">
            <label class="filter-label">Tobacco Type</label>
            <div class="d-flex align-items-center" style="gap: 8px">
              <div class="type-toggle">
                <button
                  type="button"
                  class="type-toggle-btn"
                  :class="{ active: type === null }"
                  @click="setType(null)"
                >
                  All
                </button>
                <button
                  type="button"
                  class="type-toggle-btn"
                  :class="{ active: type === 'T' }"
                  @click="setType('T')"
                >
                  PMI
                </button>
                <button
                  type="button"
                  class="type-toggle-btn"
                  :class="{ active: type === 'P' }"
                  @click="setType('P')"
                >
                  POOL
                </button>
              </div>
              <b-button
                variant="outline-success"
                size="sm"
                class="ml-auto text-nowrap"
                :disabled="!cards.length"
                @click="downloadExcel"
              >
                <feather-icon icon="DownloadCloudIcon" size="14" class="mr-50" />
                Download Excel
              </b-button>
            </div>
          </b-col>
        </b-row>

        <small v-if="creatingError" class="text-danger d-block mt-1">{{ creatingError }}</small>
      </b-card-body>
    </b-card>
    <!-- ./Filters -->

    <!-- ── Summary metrics ─────────────────────────────────────────────────── -->
    <b-row v-if="cards.length" class="mb-1">
      <b-col cols="6" md="4">
        <b-card class="metric-card metric-card-blue mb-1 mb-md-0">
          <div class="metric-icon metric-icon-blue">
            <feather-icon icon="LayersIcon" size="16" />
          </div>
          <div class="metric-label">Stacks</div>
          <div class="metric-value text-primary">{{ totalStacks.toLocaleString() }}</div>
        </b-card>
      </b-col>
      <b-col cols="6" md="4">
        <b-card class="metric-card metric-card-amber mb-1 mb-md-0">
          <div class="metric-icon metric-icon-amber">
            <feather-icon icon="PackageIcon" size="16" />
          </div>
          <div class="metric-label">Total bales</div>
          <div class="metric-value text-warning">{{ totalBales.toLocaleString() }}</div>
        </b-card>
      </b-col>
      <b-col cols="12" md="4">
        <b-card class="metric-card metric-card-green mb-0">
          <div class="metric-icon metric-icon-green">
            <feather-icon icon="TrendingUpIcon" size="16" />
          </div>
          <div class="metric-label">Total mass</div>
          <div class="metric-value text-success">{{ totalMass.toLocaleString() }} <small>kg</small></div>
        </b-card>
      </b-col>
    </b-row>

    <!-- ── States ──────────────────────────────────────────────────────────── -->
    <div v-if="!stackGrade.warehouse_id" class="empty-state">
      <feather-icon icon="FilterIcon" size="30" class="empty-state-icon" />
      <div class="empty-state-title">Choose a location and warehouse</div>
      <div class="empty-state-sub">Stack cards will appear here once a warehouse is selected above.</div>
    </div>

    <div v-else-if="loading" class="empty-state">
      <b-spinner class="mb-1" />
      <div class="empty-state-title">Loading stacks...</div>
    </div>

    <div v-else-if="!cards.length" class="empty-state">
      <feather-icon icon="InboxIcon" size="30" class="empty-state-icon" />
      <div class="empty-state-title">No graded stacks found</div>
      <div class="empty-state-sub">Try a different warehouse or tobacco type.</div>
    </div>

    <!-- ── Stack cards ─────────────────────────────────────────────────────── -->
    <b-row v-else>
      <b-col
        v-for="card in cards"
        :key="card.name"
        cols="12"
        sm="6"
        lg="4"
        xl="3"
        class="mb-2"
      >
        <b-card no-body class="stack-card mb-0">
          <div class="stack-card-accent" :style="{ backgroundColor: card.color }" />
          <div class="stack-card-body">
            <div class="stack-card-head">
              <span class="stack-name">{{ card.name }}</span>
              <span class="stack-grade-count">{{ card.grades.length }} grade{{ card.grades.length === 1 ? '' : 's' }}</span>
            </div>

            <div class="stack-stats">
              <div class="stack-stat">
                <div class="stack-stat-value">{{ card.bales.toLocaleString() }}</div>
                <div class="stack-stat-label">Bales</div>
              </div>
              <div class="stack-stat">
                <div class="stack-stat-value">{{ card.mass.toLocaleString() }}</div>
                <div class="stack-stat-label">Kg</div>
              </div>
            </div>

            <div v-if="card.grades.length" class="stack-grades">
              <b-badge
                v-for="grade in card.grades"
                :key="grade"
                variant="light-secondary"
                class="stack-grade-badge"
              >
                {{ grade }}
              </b-badge>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
    <!-- ./Stack cards -->
  </div>
</template>

<script>
import { ValidationProvider } from 'vee-validate'
import {
    BRow, BCol, BCard, BCardBody,
    BButton, BFormSelect, BFormSelectOption,
    BSpinner, BBadge,
} from 'bootstrap-vue'
import * as XLSX from 'xlsx'
import LocationFilters from '@core/components/cromis/filters/LocationFilters.vue'
import store from '@/store'
import { ref, computed, onUnmounted } from '@vue/composition-api'

import stackSummaryStoreModule from '@/views/cromis/warehouse/stack_summary/stackSummaryStoreModule'
import useStackSummaryList from '@/views/cromis/warehouse/stack_summary/useStackSummaryList'

export default {
    components: {
        BRow,
        BCol,
        BCard,
        BCardBody,
        BButton,
        BFormSelect,
        BFormSelectOption,
        BSpinner,
        BBadge,
        ValidationProvider,
        LocationFilters,
    },
    setup() {
        const CROMIS_STORE_MODULE_NAME = 'cromis-stack'
        if (!store.hasModule(CROMIS_STORE_MODULE_NAME)) store.registerModule(CROMIS_STORE_MODULE_NAME, stackSummaryStoreModule)
        onUnmounted(() => {
            if (store.hasModule(CROMIS_STORE_MODULE_NAME)) store.unregisterModule(CROMIS_STORE_MODULE_NAME)
        })

        const { locationFilter } = useStackSummaryList()

        const loading = ref(false)
        const serverErrors = ref(null)
        const creatingError = ref(null)
        const warehouses = ref([])
        const stacks = ref([])
        const type = ref(null)

        const colors = ['#185fa5', '#639922', '#ef9f27', '#a32d2d', '#6d4fa3', '#0e8f7a', '#c2461f', '#3a6ea5']

        const stackGrade = ref({
            warehouse_id: null,
        })

        const changeFilter = async filteredValue => {
            locationFilter.value = filteredValue !== null ? filteredValue.id : null
            stackGrade.value.warehouse_id = null
            stacks.value = []

            if (locationFilter.value === null) {
                warehouses.value = []
                return
            }

            await store.dispatch(`${CROMIS_STORE_MODULE_NAME}/getWarehouse`, {
                location_id: locationFilter.value,
                storage_only: true,
                minify: true,
            })
                .then(response => {
                    warehouses.value = response.data.warehouses
                })
                .catch(error => {
                    console.log('got error', error)
                })
        }

        const getStack = async () => {
            if (!stackGrade.value.warehouse_id) return

            loading.value = true
            creatingError.value = null

            await store.dispatch(`${CROMIS_STORE_MODULE_NAME}/getStack`, {
                warehouse_id: stackGrade.value.warehouse_id,
                type: type.value,
            })
                .then(response => {
                    if (!response.data?.warehouses || !Array.isArray(response.data.warehouses)) {
                        creatingError.value = 'Unexpected response while loading stacks.'
                        stacks.value = []
                        return
                    }

                    stacks.value = response.data.warehouses
                })
                .catch(error => {
                    creatingError.value = 'Could not load stacks for this warehouse. Please try again.'
                    stacks.value = []
                    console.log('got error', error)
                })

            loading.value = false
        }

        const setType = value => {
            type.value = value
            getStack()
        }

        // `groups` is an array of grade-group assignments for the stack (see
        // StackGrade.vue, which reads the same warehouses_min payload) — a stack
        // with no grade assigned yet is left off the summary.
        const cards = computed(() => stacks.value
            .filter(item => Array.isArray(item.groups) && item.groups.length > 0)
            .map((item, index) => ({
                name: item.name,
                bales: item.stock?.stockBales || 0,
                mass: item.stock?.stockMass > 0 ? item.stock.stockMass : 0,
                grades: item.groups.map(group => group.name),
                color: colors[index % colors.length],
            })))

        const totalStacks = computed(() => cards.value.length)
        const totalBales = computed(() => cards.value.reduce((sum, card) => sum + card.bales, 0))
        const totalMass = computed(() => cards.value.reduce((sum, card) => sum + card.mass, 0))

        const downloadExcel = () => {
            if (!cards.value.length) return

            const rows = cards.value.map(card => ({
                'Stack Name': card.name,
                Bales: card.bales,
                'Mass (Kg)': card.mass,
                Grades: card.grades.join(', '),
            }))

            const sheet = XLSX.utils.json_to_sheet(rows)
            sheet['!cols'] = [{ wch: 18 }, { wch: 10 }, { wch: 14 }, { wch: 30 }]

            const workbook = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(workbook, sheet, 'Stack Summary')

            const warehouse = warehouses.value.find(w => w.id === stackGrade.value.warehouse_id)
            const fileSuffix = warehouse ? warehouse.name.replace(/\s+/g, '_') : 'warehouse'
            XLSX.writeFile(workbook, `stack_summary_${fileSuffix}.xlsx`)
        }

        return {
            serverErrors,
            creatingError,
            loading,
            warehouses,
            stackGrade,
            type,
            cards,
            totalStacks,
            totalBales,
            totalMass,
            changeFilter,
            getStack,
            setType,
            downloadExcel,
        }
    },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';

.page-section-header {
  padding-bottom: 0.85rem;
  border-bottom: 0.5px solid #ebe9f1;

  .page-header-icon {
    width: 42px; height: 42px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    background: linear-gradient(135deg, #e6f1fb, #d9ecf9);
    color: #185fa5;
  }
}

.filter-card {
  border-radius: 0.6rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

.filter-label {
  display: block;
  font-size: 12px; font-weight: 500; color: #5e5873; margin-bottom: 6px;
}

// ── Tobacco type toggle ──────────────────────────────────────────────────────
.type-toggle {
  display: inline-flex; border: 1px solid #d8d6de; border-radius: 6px; overflow: hidden; flex-shrink: 0;

  .type-toggle-btn {
    border: none; background: #fff; color: #6e6b7b;
    font-size: 12px; padding: 6px 12px; cursor: pointer;
    border-right: 1px solid #d8d6de;
    &:last-child { border-right: none; }
    &:hover { background: #f4f4f5; }
    &.active { background: #185fa5; color: #fff; }
  }
}

// ── Metric cards ───────────────────────────────────────────────────────────
.metric-card {
  border-radius: 0.6rem;
  border-top: 3px solid transparent;
  box-shadow: 0 4px 18px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.05);
  transition: box-shadow 0.2s;

  &:hover { box-shadow: 0 8px 28px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06); }

  ::v-deep .card-body { padding: 1rem 1.1rem; }

  &.metric-card-blue  { border-top-color: #185fa5; }
  &.metric-card-amber { border-top-color: #ef9f27; }
  &.metric-card-green { border-top-color: #639922; }

  .metric-icon {
    width: 34px; height: 34px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center; margin-bottom: 10px;
    &.metric-icon-blue  { background: #e6f1fb; color: #185fa5; }
    &.metric-icon-amber { background: #faeeda; color: #854f0b; }
    &.metric-icon-green { background: #eaf3de; color: #3b6d11; }
  }

  .metric-label {
    font-size: 11px; color: #b9b9c3;
    text-transform: uppercase; letter-spacing: 0.35px; margin-bottom: 5px;
  }
  .metric-value { font-size: 22px; font-weight: 500; line-height: 1.2; }
}

// ── Empty state ──────────────────────────────────────────────────────────
.empty-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; padding: 56px 20px;
  .empty-state-icon { color: #d8d6de; margin-bottom: 10px; }
  .empty-state-title { font-size: 14px; font-weight: 500; color: #5e5873; }
  .empty-state-sub { font-size: 12px; color: #b9b9c3; margin-top: 4px; max-width: 360px; }
}

// ── Stack cards ────────────────────────────────────────────────────────────
.stack-card {
  border-radius: 0.6rem;
  box-shadow: 0 4px 18px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.05);
  overflow: hidden;
  transition: box-shadow 0.2s;
  &:hover { box-shadow: 0 8px 28px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06); }

  .stack-card-accent { height: 4px; width: 100%; }

  .stack-card-body { padding: 14px 16px; }

  .stack-card-head {
    display: flex; align-items: baseline; justify-content: space-between; gap: 8px;
    margin-bottom: 12px;
  }
  .stack-name {
    font-size: 18px; font-weight: 600; color: #5e5873;
    word-break: break-word; line-height: 1.2;
  }
  .stack-grade-count {
    font-size: 10px; color: #b9b9c3; white-space: nowrap;
  }

  .stack-stats {
    display: flex; gap: 10px; margin-bottom: 12px;
  }
  .stack-stat {
    flex: 1; background: #f8f8f8; border-radius: 6px; padding: 8px 10px;
  }
  .stack-stat-value { font-size: 16px; font-weight: 600; color: #5e5873; line-height: 1.2; }
  .stack-stat-label {
    font-size: 10px; color: #b9b9c3;
    text-transform: uppercase; letter-spacing: 0.3px; margin-top: 2px;
  }

  .stack-grades {
    display: flex; flex-wrap: wrap; gap: 4px;
  }
  .stack-grade-badge { font-size: 10px; padding: 3px 7px; }
}
</style>
